/**
 * ต้นทางที่ใช้ `<PricingBar />`:
 * - `app/service/[id]/FirstResponder.tsx` — หน้า service (รวม embedded iframe); ส่ง `serviceId`, `workerData.pricing`, `packages`, ฯลฯ
 * - `app/components/WorkerCard.tsx` — โค้ดส่ง props แบบเดียวกัน (ใน repo นี้ยังไม่พบไฟล์อื่น import `WorkerCard` ตัวนี้; ที่ใช้ใน UI หลักคือ `workercard/WorkerCard.tsx` ซึ่งไม่มี PricingBar)
 *
 * แพ็กเกจ: มี `serviceId` จะดึง `GET /services/:id` แล้วเลือกแพ็กฟรี (price 0 + displayOrder 0) — แสดง `packages.dailyPrice` และ `packages.transactionPrice` จาก API
 */
import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { servicesApi, type Service } from "@/lib/api/index";
import { navigateToSignin } from "@/lib/openLineExternalBrowser";
import { useWorkspaceStatus } from "@/hooks/useWorkspaceStatus";
import { AeraButton } from "@/app/components/ui/AeraButton";

export interface Package {
  id?: string;
  name: string;
  price: number;
  /** จาก API — ราคาต่อวัน */
  dailyPrice?: number;
  /** จาก API — ราคาต่อธุรกรรม */
  transactionPrice?: number;
  capacity: string;
  /** จาก API — ใช้เลือกแพ็กเกจหลัก (เช่น ฟรี displayOrder 0) */
  displayOrder?: number;
}

/**
 * เลือกแพ็กเกจสำหรับแถบราคา: ตรงกับ API — price 0 + displayOrder 0 (เช่น "ฟรี 10 รายการต่อเดือน")
 * ถ้าไม่มี ให้หาแพ็กฟรีตามชื่อ / เรียง displayOrder แล้วเอาตัวแรก
 */
export function selectDisplayPackage(packages: Package[]): Package | null {
  if (packages.length === 0) return null;

  const freeTier =
    packages.find(
      (p) => (p.displayOrder ?? 0) === 0 && Number(p.price) === 0,
    ) ??
    packages.find(
      (p) =>
        Number(p.price) === 0 &&
        (p.name.includes("ฟรี") || p.name.includes("ฟรี 10")),
    );

  if (freeTier) return freeTier;

  const sorted = [...packages].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );
  return sorted[0] ?? null;
}

export function unwrapServiceResponse(response: unknown): Service | null {
  if (!response || typeof response !== "object") return null;
  if ("service" in response) {
    return (response as { service?: Service }).service ?? null;
  }
  if ("data" in response) {
    return (response as { data?: Service }).data ?? null;
  }
  return response as Service;
}

/** จำนวนงาน/เดือน: ใช้ capacity จาก API ก่อน ถ้าเป็น 0 ให้ดึงตัวเลขจากชื่อแพ็กเกจ (เช่น "ฟรี 10 รายการต่อเดือน") */
function parseTaskCountFromPackage(pkg: Package | null): number {
  if (!pkg) return 0;
  const cap = parseInt(String(pkg.capacity ?? "").trim(), 10);
  if (Number.isFinite(cap) && cap > 0) return cap;
  const m = pkg.name.match(/(\d[\d,]*)/);
  if (m) {
    const n = parseInt(m[1].replace(/,/g, ""), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

interface PricingBarProps {
  price: string;
  tasksIncluded: string;
  savings: string;
  showAddButton?: boolean;
  title?: string;
  serviceId?: string;
  packageId?: string;
  workspaceId?: string;
  packages?: Package[];
  thumbnail?: string;
  expertName?: string;
  expertTitle?: string;
  onHireRequest?: () => void;
}

export default function PricingBar({
  price,
  tasksIncluded,
  showAddButton = false,
  title,
  serviceId,
  packageId,
  workspaceId,
  packages = [],
  thumbnail,
  expertName,
  expertTitle,
  onHireRequest,
}: PricingBarProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const router = useRouter();
  const { workspaces } = useWorkspaceStatus({
    serviceId,
    workspaceId,
  });

  const targetWorkspaceId = workspaceId ?? workspaces?.[0]?.id;

  /** null = ยังไม่โหลด / ไม่มี serviceId — ใช้ props ชั่วคราว; array = ผลจาก API จริง (อาจว่าง) */
  const [fetchedPackages, setFetchedPackages] = useState<Package[] | null>(
    null,
  );

  /** หลังได้ response จาก API แล้วใช้ของ API เท่านั้น; ก่อนนั้น fallback จาก parent */
  const effectivePackages =
    fetchedPackages !== null ? fetchedPackages : packages;

  useEffect(() => {
    const checkAuth = () => {
      if (typeof window !== "undefined") {
        const token = localStorage.getItem("auth_token");
        setIsAuthenticated(!!token?.trim());
      }
    };

    checkAuth();

    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener("auth:loginSuccess", handleAuthChange);
    window.addEventListener("auth:login", handleAuthChange);
    window.addEventListener("auth:logout", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);

    return () => {
      window.removeEventListener("auth:loginSuccess", handleAuthChange);
      window.removeEventListener("auth:login", handleAuthChange);
      window.removeEventListener("auth:logout", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    if (!serviceId) {
      setFetchedPackages(null);
      return;
    }

    let cancelled = false;
    setFetchedPackages(null);

    (async () => {
      try {
        const response = await servicesApi.getById(serviceId);
        const service = unwrapServiceResponse(response);
        const raw = service?.packages;
        if (!Array.isArray(raw)) {
          if (!cancelled) setFetchedPackages([]);
          return;
        }

        const mapped: Package[] = raw.map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          price: Number(pkg.price) || 0,
          dailyPrice:
            typeof pkg.dailyPrice === "number"
              ? pkg.dailyPrice
              : Number(pkg.dailyPrice) || 0,
          transactionPrice:
            typeof pkg.transactionPrice === "number"
              ? pkg.transactionPrice
              : Number(pkg.transactionPrice) || 0,
          capacity: String(pkg.capacity ?? "0"),
          displayOrder:
            typeof pkg.displayOrder === "number" ? pkg.displayOrder : 0,
        }));

        if (!cancelled) setFetchedPackages(mapped);
      } catch {
        if (!cancelled) setFetchedPackages([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  /** แพ็กเกจที่มีราคา — ใช้โชว์ราคาขีดฆ่าเมื่อแพ็กเกจปัจจุบันฟรี */
  const paidPackageRef = useMemo(
    () => effectivePackages.find((p) => (Number(p.price) || 0) > 0),
    [effectivePackages],
  );

  if (!isAuthenticated) {
    const handleLogin = () => {
      const currentPath =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : "/";
      navigateToSignin(
        router,
        `/signin?callbackUrl=${encodeURIComponent(
          currentPath,
        )}&login_required=1&reason=access_app`,
      );
    };

    return (
      <div className="w-full absolute bottom-0 bg-aera-white border-t border-gray-200 p-4">
        <div className="container mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-aera-slate">
              กรุณาเข้าสู่ระบบก่อนใช้งาน
            </p>
            <p className="text-xs text-aera-slate">
              เพื่อกด “จ้าง Worker” และเริ่มทำงาน
            </p>
          </div>
          <AeraButton
            variant="primary"
            size="lg"
            onClick={handleLogin}
            className="shadow-lg shadow-aera-amberlight/10 hover:bg-aera-charcoal hover:text-aera-white"
          >
            Log in
          </AeraButton>
        </div>
      </div>
    );
  }

  /** แพ็กเกจหลักจาก API: ฟรี + displayOrder 0 (เช่น ฟรี 10 รายการต่อเดือน) — ไม่ใช้แค่ index 0 */
  const currentPackage = selectDisplayPackage(effectivePackages);

  /** `package.price` — ใช้ตรวจแพ็กฟรี / fallback เท่านั้น */
  const packagePrice = currentPackage
    ? Number(currentPackage.price)
    : parseFloat(price.replace(/[^0-9.]/g, "")) || 0;

  /** แถบราคา: แถวบน = `packages.dailyPrice`, แถวล่าง = `packages.transactionPrice` */
  const packageDailyPrice = currentPackage
    ? Number(currentPackage.dailyPrice ?? 0)
    : 0;
  const packageTransactionPrice = currentPackage
    ? Number(currentPackage.transactionPrice ?? 0)
    : 0;

  const tasksPerMonth = currentPackage
    ? parseTaskCountFromPackage(currentPackage)
    : parseInt(String(tasksIncluded).replace(/\D/g, ""), 10) || 0;

  const strikethroughDailyValue =
    paidPackageRef && Number(paidPackageRef.dailyPrice ?? 0) > 0
      ? Number(paidPackageRef.dailyPrice).toFixed(2)
      : "";
  const showStrikethroughDailyPrice =
    packagePrice === 0 &&
    paidPackageRef != null &&
    strikethroughDailyValue !== "";

  const strikethroughTransactionValue =
    paidPackageRef && Number(paidPackageRef.transactionPrice ?? 0) > 0
      ? Number(paidPackageRef.transactionPrice).toFixed(2)
      : "";
  const showStrikethroughTransactionPrice =
    packagePrice === 0 &&
    paidPackageRef != null &&
    strikethroughTransactionValue !== "";

  const displayPrice = currentPackage ? `฿${currentPackage.price}` : price;
  const displayTasks = currentPackage
    ? tasksPerMonth > 0
      ? String(tasksPerMonth)
      : currentPackage.capacity
    : tasksIncluded;
  const currentPackageId = currentPackage?.id || packageId;

  return (
    <div className="w-full h-[103px] absolute bottom-0 bg-aera-white border-t border-gray-200 p-4">
      <div className="w-full flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0">
          <div className="flex flex-wrap items-baseline gap-1 text-sm text-aera-slate">
            <span className="text-2xl font-semibold text-aera-slate">
              {currentPackage ? `${packageTransactionPrice.toFixed(2)}` : "—"}
            </span>
            {showStrikethroughTransactionPrice &&
              strikethroughTransactionValue && (
                <span className="line-through">{`${strikethroughTransactionValue}`}</span>
              )}
            <span>เครดิต / ข้อความ</span>
          </div>
          <div className="flex flex-wrap items-baseline gap-1 text-sm text-aera-slate">
            <span className="text-md font-bold text-aera-slate">
              {currentPackage ? `${packageDailyPrice.toFixed(2)}` : "—"}
            </span>
            {showStrikethroughDailyPrice && (
              <span className="line-through">{`${strikethroughDailyValue}`}</span>
            )}
            <span>เครดิต / วัน</span>
          </div>

          {packagePrice === 0 && currentPackage?.name?.trim() && (
            <p className="text-[12px] font-bold text-aera-amberdark">
              {currentPackage.name}
            </p>
          )}
        </div>
        {
          <AeraButton
            variant="primary"
            fontWeight="medium"
            onClick={() => {
              if (!showAddButton) {
                if (!serviceId || !currentPackageId) {
                  return;
                }

                if (onHireRequest) {
                  onHireRequest();
                }

                if (typeof window !== "undefined") {
                  const message = {
                    type: "OPEN_HIRE_MODAL",
                    props: {
                      serviceId,
                      packageId: currentPackageId,
                      ...(targetWorkspaceId
                        ? { workspaceId: targetWorkspaceId }
                        : {}),
                      workerData: {
                        title: title || "AI Worker Service",
                        packageName: currentPackage?.name,
                        price: displayPrice,
                        capacity: displayTasks,
                        expertName,
                        expertTitle,
                        thumbnail,
                      },
                    },
                  };

                  if (!window.parent) {
                    console.warn(
                      "[PricingBar] window.parent is missing; cannot postMessage",
                    );
                    return;
                  }

                  window.parent.postMessage(message, "*");
                } else {
                  console.warn("[PricingBar] No window (SSR); click ignored");
                }
              }
            }}
          >
            จ้าง Worker
          </AeraButton>
        }
      </div>
    </div>
  );
}
